// The picture of a thermometer and of a digital clock. Plain React SVG,
// like practice-graphs.tsx.
//
// The thermometer never prints its reading: the dark column of liquid
// stops against the scale and the child reads it off. The clock is drawn
// in the seven strokes of a real display.

import {
  THERMOMETER_LABEL_SIZE,
  clockPicture,
  describeDigitalClock,
  describeThermometer,
  thermometerPicture,
  type DigitalClockSpec,
  type ThermometerSpec,
} from './practice-instrument-data';

const INK = '#2d4a3e';
const PANEL_FILL = '#eef3ee';

function ThermometerView({ thermometer }: { thermometer: ThermometerSpec }) {
  const picture = thermometerPicture(thermometer);
  const { x, half, bulb } = picture;
  // Where the tube meets the round bulb.
  const join = bulb.y - Math.sqrt(bulb.radius ** 2 - half ** 2);
  const round = picture.tubeTop + half;

  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={describeThermometer(thermometer)}
        viewBox={`0 0 ${String(picture.width)} ${String(picture.height)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(picture.width * 1.4))}px` }}
      >
        <text
          data-unit="true"
          x={x}
          y="14"
          textAnchor="middle"
          fill={INK}
          fontSize={THERMOMETER_LABEL_SIZE}
          fontWeight="700"
        >
          {thermometer.unit}
        </text>
        <path
          data-tube="true"
          d={`M ${String(x - half)} ${String(round)} A ${String(half)} ${String(half)} 0 0 1 ${String(x + half)} ${String(round)} L ${String(x + half)} ${join.toFixed(2)} A ${String(bulb.radius)} ${String(bulb.radius)} 0 1 1 ${String(x - half)} ${join.toFixed(2)} Z`}
          fill="#ffffff"
          stroke={INK}
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <circle cx={x} cy={bulb.y} r={bulb.radius - 4} fill={INK} />
        <rect
          data-liquid="true"
          x={x - half + 3.5}
          y={picture.liquidTop}
          width={(half - 3.5) * 2}
          height={bulb.y - picture.liquidTop}
          fill={INK}
        />
        {picture.ticks.map((tick) => (
          <line
            key={tick.value}
            data-tick={tick.label === null ? 'short' : 'long'}
            x1={picture.tickFrom}
            y1={tick.y}
            x2={picture.tickFrom + (tick.label === null ? 7 : 12)}
            y2={tick.y}
            stroke={INK}
            strokeWidth={tick.label === null ? '1.2' : '1.8'}
          />
        ))}
        {picture.ticks.map(
          (tick) =>
            tick.label !== null && (
              <text
                key={tick.value}
                data-number="true"
                x={picture.labelX}
                y={tick.y}
                dy="0.35em"
                fill={INK}
                fontSize={THERMOMETER_LABEL_SIZE}
              >
                {tick.label}
              </text>
            ),
        )}
      </svg>
    </figure>
  );
}

function DigitalClockView({ clock }: { clock: DigitalClockSpec }) {
  const picture = clockPicture(clock);

  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={describeDigitalClock(clock)}
        viewBox={`0 0 ${String(picture.width)} ${String(picture.height)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(picture.width * 1.2))}px` }}
      >
        <rect
          x="1.5"
          y="1.5"
          width={picture.width - 3}
          height={picture.height - 3}
          rx="10"
          fill={PANEL_FILL}
          stroke={INK}
          strokeWidth="3"
        />
        {picture.digits.map((digit, index) => (
          <g key={index} data-digit={digit.digit}>
            {digit.strokes.map((points) => (
              <polygon key={points} points={points} fill={INK} />
            ))}
          </g>
        ))}
        {picture.dots.map((dot) => (
          <rect
            key={dot.y}
            data-colon="true"
            x={dot.x}
            y={dot.y}
            width={dot.size}
            height={dot.size}
            fill={INK}
          />
        ))}
      </svg>
    </figure>
  );
}

export function InstrumentPictureView({
  instrument,
}: {
  instrument: ThermometerSpec | DigitalClockSpec;
}) {
  return instrument.type === 'thermometer' ? (
    <ThermometerView thermometer={instrument} />
  ) : (
    <DigitalClockView clock={instrument} />
  );
}
